/**
 * F-138: what a storyboard's B-roll link says about TIME — the helpers a
 * clip range needs, shared by the link card's editor (client), the
 * storyboard export (client + desktop) and, later, a server route.
 *
 * PURE: no Yjs, no DOM, no fetch. The server's unfurl module has its own
 * `isYouTubeUrl` over a `URL` for oEmbed routing; this one takes the stored
 * string and answers the storyboard's question, "is this card a video an
 * agent can download a stretch of?", which is close enough to the same test
 * that the host list is copied rather than abstracted across packages.
 */
/** Hosts whose URLs are YouTube videos (`www.` stripped before matching). */
const YOUTUBE_HOSTS = new Set([
    "youtube.com",
    "m.youtube.com",
    "music.youtube.com",
    "youtube-nocookie.com",
]);
const { URL: WhatwgURL, URLSearchParams: WhatwgSearchParams } = globalThis;
function parseHttpUrl(url) {
    let parsed;
    try {
        parsed = new WhatwgURL(url);
    }
    catch {
        return null;
    }
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed : null;
}
/**
 * Does this stored URL address one YouTube VIDEO (watch, youtu.be, shorts,
 * embed, live, the old /v/ form)? Playlists are not videos and answer false.
 */
export function isYouTubeVideoUrl(url) {
    const parsed = parseHttpUrl(url);
    if (parsed === null)
        return false;
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    if (host === "youtu.be")
        return parsed.pathname.length > 1;
    if (!YOUTUBE_HOSTS.has(host))
        return false;
    if (parsed.pathname === "/watch")
        return parsed.searchParams.get("v") !== null;
    return /^\/(shorts|embed|live|v)\/[^/]+/.test(parsed.pathname);
}
/**
 * The `t=` / `start=` a YouTube link carries, in milliseconds — "1m30s",
 * "90", "90s", "1h2m3s" — or undefined. Also read from the fragment
 * (`#t=90`), the form the old share dialog wrote. A link pasted "at 1:30"
 * is a writer saying where the clip starts, so the storyboard reads it as
 * the start when no explicit range was set. Never an end: YouTube has no
 * end parameter outside embeds, and an `end=` there is rare enough that a
 * writer who wants one sets the range on the card.
 */
export function youtubeStartMs(url) {
    const parsed = parseHttpUrl(url);
    if (parsed === null || !isYouTubeVideoUrl(url))
        return undefined;
    const fragment = new WhatwgSearchParams(parsed.hash.replace(/^#/, ""));
    const raw = parsed.searchParams.get("t") ?? parsed.searchParams.get("start") ?? fragment.get("t");
    if (raw === null)
        return undefined;
    return parseYoutubeTime(raw);
}
/** "1h2m3s" | "2m3s" | "3s" | "3" → ms; anything else undefined. */
function parseYoutubeTime(raw) {
    const text = raw.trim().toLowerCase();
    if (/^\d+$/.test(text))
        return Number(text) * 1000;
    const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(text);
    if (match === null || text.length === 0)
        return undefined;
    const [, h, m, s] = match;
    if (h === undefined && m === undefined && s === undefined)
        return undefined;
    return ((Number(h ?? 0) * 60 + Number(m ?? 0)) * 60 + Number(s ?? 0)) * 1000;
}
/**
 * A clip time as people write it — "1:23", "01:23.5", "1:02:03", "83" (bare
 * seconds), "1m23s" — to milliseconds, or undefined for anything else. Sub-
 * second precision is kept to the millisecond; a minute or second field over
 * 59 in a colon form is refused (it is a typo, not a convention).
 */
export function parseClipTime(input) {
    const text = input.trim().toLowerCase();
    if (text.length === 0)
        return undefined;
    if (/^\d+(\.\d+)?$/.test(text))
        return Math.round(Number(text) * 1000);
    const ytStyle = parseYoutubeTime(text);
    if (ytStyle !== undefined)
        return ytStyle;
    const parts = text.split(":");
    if (parts.length < 2 || parts.length > 3)
        return undefined;
    if (!parts.every((part, i) => (i === parts.length - 1 ? /^\d+(\.\d+)?$/ : /^\d+$/).test(part))) {
        return undefined;
    }
    const numbers = parts.map(Number);
    // Every field but the leading one is a sexagesimal digit pair.
    if (numbers.slice(1).some((n) => n >= 60))
        return undefined;
    let seconds = 0;
    for (const n of numbers)
        seconds = seconds * 60 + n;
    return Math.round(seconds * 1000);
}
/**
 * Milliseconds as a clip time: "1:23", "1:02:03", with the fraction only
 * when there is one ("1:23.5"). What the card shows and what the export
 * writes — one shape for a human and an agent alike.
 */
export function formatClipTime(ms) {
    const total = Math.max(0, ms) / 1000;
    const whole = Math.floor(total);
    const fraction = Math.round((total - whole) * 1000);
    const h = Math.floor(whole / 3600);
    const m = Math.floor((whole % 3600) / 60);
    const s = whole % 60;
    const mmss = h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`;
    return fraction > 0 ? `${mmss}.${String(fraction).padStart(3, "0").replace(/0+$/, "")}` : mmss;
}
function pad2(n) {
    return String(n).padStart(2, "0");
}
//# sourceMappingURL=video-links.js.map